import '../layout/theme';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { TrackMyOrder } from '@jbhi-fi/components/src/views/TrackMyOrder/TrackMyOrder';
import { SimpleBreadcrumbsProps } from '@jbhi-fi/components/src/SimpleBreadcrumbs/SimpleBreadcrumbs';
import { RecaptchaProvider } from '@jbhi-fi/components/src/context/RecaptchaProvider';
import { PhoneHelper } from '../utils/phoneHelper/PhoneHelper';
import { trackingContext, TrackingProvider } from '@jbhi-fi/components/src/Tracking';
import { BaseContentProvider } from './components/stack/components/BaseContentProvider/BaseContentProvider';
import { DefaultLayout } from './components/layout/DefaultLayout';

const ORDER_ID_PARAM = 'orderId';

export const buildBreadcrumbs = (orderNumber?: string): SimpleBreadcrumbsProps => {
  const defaultBreadcrumbs = [
    { label: 'Home', href: '/' },
    { label: 'Track my order', href: '/pages/track-my-order' },
  ];

  const mobileBreadcrumb = [{ label: 'Track another order', href: '/pages/track-my-order' }];

  if (orderNumber) {
    return {
      breadcrumbs: {
        md: [
          ...defaultBreadcrumbs,
          {
            label: `Order #${orderNumber}`,
          },
        ],
        xs: mobileBreadcrumb,
      },
    };
  }
  return { breadcrumbs: { md: defaultBreadcrumbs, xs: mobileBreadcrumb } };
};

const validateMobile = async (
  mobileNumber: string,
  countryCode: string
): Promise<{ isMobileValid: boolean; formattedMobile: string; error?: string }> => {
  const validationResponse = await PhoneHelper.validateWithApi({ phoneNumber: mobileNumber, countryCode });
  const countryCodeNumber = countryCode === 'NZ' ? '+64' : '+61';
  const isMobile =
    !validationResponse.e164Format?.startsWith(countryCodeNumber) || validationResponse.phoneType === 'MOBILE';
  const getError = () => {
    if (!PhoneHelper.validateNumberLength(mobileNumber)) return "That mobile number doesn't look quite right.";
    if (!isMobile && !mobileNumber.startsWith('+'))
      return 'For international numbers please add the + before your country code e.g. +513';
    return undefined;
  };

  return {
    isMobileValid: isMobile && validationResponse.isValid,
    formattedMobile: validationResponse?.e164Format ?? '',
    error: getError(),
  };
};

const getOrderIdFromUrl = () => new URL(window.location.href).searchParams?.get(ORDER_ID_PARAM);

const setOrderIdOnUrl = (orderId?: string) => {
  const url = new URL(window.location.href);
  if (orderId) {
    url.searchParams.set(ORDER_ID_PARAM, orderId);
  } else {
    url.searchParams.delete(ORDER_ID_PARAM);
  }
  window.history.replaceState({}, '', url.toString());
};

document.addEventListener('DOMContentLoaded', () => {
  const container = document.getElementById('orderTracking-container');
  const settings = window.Jbhifi.orderTrackingSettings ?? {};
  if (container) {
    const root = createRoot(container);
    root.render(
      <BaseContentProvider>
        <TrackingProvider group="order-tracking" context={trackingContext.orderTracking}>
          <RecaptchaProvider siteKey={window.Jbhifi?.recaptchaSiteKey ?? ''}>
            <DefaultLayout>
              <TrackMyOrder
                environment={window.Jbhifi.storeLocation}
                settings={settings}
                buildBreadcrumbs={buildBreadcrumbs}
                validateMobile={validateMobile}
                verifiedOrderId={getOrderIdFromUrl() || undefined}
                setVerifiedOrderId={setOrderIdOnUrl}
              />
            </DefaultLayout>
          </RecaptchaProvider>
        </TrackingProvider>
      </BaseContentProvider>
    );
  }
});
